import { Divider, Skeleton, Stack, Typography } from '@mui/material'
import { SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
  PreSaleLotteryConfirmationFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { ApplyPreSaleLotteryValue } from '../../LotteryDetail/template/LotteryDetailForm'
import {
  ButtonCell,
  LotteryPlansCell,
  LotterySubTitleCell,
  PaymentCell,
  PaymentInfoCell,
} from './components'

export * from './fragments'

export type State = {
  loading: boolean
  isRootAuthFailed: boolean
  submitting: boolean
  preSale: PreSaleLotteryConfirmationFragment | undefined
  paymentSource: Maybe<PaymentDetailFragment> | undefined
  choice: ApplyPreSaleLotteryValue | undefined
  billingStatements:
    | {
        statements: {
          label: Maybe<string> | undefined
          value: string
        }[]
        totalPrice: number
      }[]
    | undefined
  paymentMethod: PaymentMethod
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => Promise<void>
}

export const LotteryConfirmationTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('lotteryConfirmation')
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ preSaleId: string; clientId?: string; token?: string }>()
  return (
    <BaseLayout
      margin
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.LotteryDetail, {
          preSaleId: router.query.preSaleId ?? '#',
        }),
        title: t('title', '応募情報詳細'),
      }}
    >
      <Head title={routerNames.LotteryConfirmation} />
      {state.loading || !state.preSale ? (
        <>
          <LotteryTitleSkeletonCell />
          <LotterySubTitleSkeletonCell />
          <LotteryPlansSkeletonCell />
          <PaymentInfoSkeletonCell />
          <PaymentSkeletonCell />
          <ButtonSkeletonCell />
        </>
      ) : (
        <>
          <LotteryTitleCell />
          <LotterySubTitleCell preSale={state.preSale} />
          <LotteryPlansCell choice={state.choice} items={state.preSale.items} />
          <PaymentInfoCell
            paymentMethod={state.paymentMethod}
            paymentSource={state.paymentSource}
          />
          <PaymentCell
            billingStatements={state.billingStatements}
            choiceLength={state.choice?.choices?.length}
          />
          <Typography variant="body2" sx={{ pb: 4 }}>
            {state.paymentMethod === PaymentMethod.Konbini
              ? t('noteByKonbini')
              : t('note')}
          </Typography>
          <ButtonCell onSubmit={onSubmit} isSubmitting={state.submitting} />
        </>
      )}
    </BaseLayout>
  )
}

const LotteryTitleCell: Cell = () => {
  const { t } = useTranslation('lotteryConfirmation')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Typography variant="h4">
        {t('confirmApplicationDetail', '応募内容の確認')}
      </Typography>
    </CellFrame>
  )
}

const LotteryTitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ py: 5 }}>
      <SkeletonText variant="h4" width="50%" />
    </CellFrame>
  )
}

const LotterySubTitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack sx={{ pt: 3 }} spacing={2}>
        {Array.from(Array(3).keys()).map(index => (
          <Stack key={index} spacing={2}>
            <Stack>
              <SkeletonText variant="body2" numberOfLine={2} />
            </Stack>
            <Divider />
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}

const LotteryPlansSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <SkeletonText variant="body2" width="10%" />
      <Stack sx={{ pt: 3 }} spacing={2}>
        {Array.from(Array(4).keys()).map(index => (
          <Stack key={index} spacing={2}>
            <Stack>
              <SkeletonText variant="body2" numberOfLine={2} />
            </Stack>
            <Divider />
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}

const PaymentInfoSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack sx={{ pt: 3 }} spacing={2}>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 5 }}>
          <Stack flex={1}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="caption" width="50%" />
          </Stack>
        </Stack>
        <Divider />
      </Stack>
    </CellFrame>
  )
}

const PaymentSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
        <SkeletonText variant="h4" />
      </Stack>
      <Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
          <SkeletonText variant="subtitle1" width="30%" />
          <SkeletonText variant="subtitle1" width="30%" />
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const ButtonSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ py: 4 }}>
      <Stack flex={1} alignItems="center">
        <Skeleton
          variant="rectangular"
          width={244}
          height={44}
          sx={{ borderRadius: '34px' }}
        />
      </Stack>
    </CellFrame>
  )
}
